import React, { useState, useEffect, useContext, useRef } from "react";
import { FiMessageSquare, FiX } from "react-icons/fi";
import { AuthContext } from "../context/AuthContext";

const SupportChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [ws, setWs] = useState(null);
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);
  const retryTimeout = useRef(null); // To track reconnection attempts

  // WebSocket URL based on environment
  const getWebSocketURL = () => {
    if (process.env.NODE_ENV === "production") {
      return "wss://www.vdptuning.com/api/chatSocket";
    } else {
      return "ws://localhost:8080/vdp/chatSocket";
    }
  };

  // Function to connect WebSocket
  const connectWebSocket = () => {
    const webSocketURL = getWebSocketURL();
    const webSocket = new WebSocket(webSocketURL);

    webSocket.onopen = () => {
      console.log("Connected to WebSocket");
    };

    webSocket.onmessage = (event) => {
      const messageData = JSON.parse(event.data);
      setMessages((prevMessages) => [...prevMessages, messageData]);
    };

    webSocket.onclose = (event) => {
      console.log("Disconnected from WebSocket. Attempting to reconnect...");
      retryWebSocketConnection(); // Attempt to reconnect
    };

    webSocket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    setWs(webSocket); // Set WebSocket in state
  };

  // Retry connection logic
  const retryWebSocketConnection = () => {
    clearTimeout(retryTimeout.current); // Clear any existing timeout
    retryTimeout.current = setTimeout(() => {
      console.log("Reconnecting WebSocket...");
      connectWebSocket(); // Attempt reconnection
    }, 3000); // Try reconnecting every 3 seconds
  };

  // Initialize WebSocket connection on component mount
  useEffect(() => {
    connectWebSocket();

    // Cleanup WebSocket connection on component unmount
    return () => {
      if (ws) {
        ws.close();
      }
      clearTimeout(retryTimeout.current); // Clear reconnection attempts on unmount
    };
  }, []);

  // Scroll to the last message in the chat
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Toggle chat box visibility
  const toggleChatBox = () => {
    setIsOpen(!isOpen);
  };

  // Send a new message via WebSocket
  const sendMessage = () => {
    if (ws && ws.readyState === WebSocket.OPEN && newMessage.trim() !== "") {
      const messageData = {
        sender: user.username,
        message: newMessage,
      };
      ws.send(JSON.stringify(messageData));
      setNewMessage(""); // Clear the input field
    } else if (ws && ws.readyState !== WebSocket.OPEN) {
      console.error("WebSocket is not open. ReadyState:", ws.readyState);
    }
  };

  // Handle pressing the "Enter" key
  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault(); // Prevent adding a new line
      sendMessage();
    }
  };

  const isMessageFromMe = (message) => message.sender === user.username;

  return (
    <div className="fixed bottom-5 right-5 z-50">
      {!isOpen && (
        <button
          onClick={toggleChatBox}
          className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700"
        >
          <FiMessageSquare size={24} />
        </button>
      )}

      {isOpen && (
        <div className="w-80 p-4 rounded-lg shadow-lg bg-white dark:bg-gray-800 dark:border-gray-700 border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Support Chat
            </h4>
            <button
              onClick={toggleChatBox}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <FiX size={20} />
            </button>
          </div>

          <div className="mb-3 h-40 overflow-y-scroll border-b border-gray-300 dark:border-gray-600 pb-3">
            {messages.length > 0 ? (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex flex-col mb-2 ${
                    isMessageFromMe(msg) ? "items-end" : "items-start"
                  }`}
                >
                  <div className="text-sm font-semibold mb-1 text-gray-600 dark:text-gray-300">
                    {isMessageFromMe(msg) ? "Me" : msg.sender}
                  </div>
                  <div
                    className={`p-2 rounded-md max-w-xs break-words overflow-wrap w-fit ${
                      isMessageFromMe(msg)
                        ? "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                        : "bg-blue-600 text-white"
                    }`}
                  >
                    {msg.message}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                No messages yet...
              </p>
            )}
            <div ref={messagesEndRef} />{" "}
            {/* Scroll anchor for the last message */}
          </div>

          <div className="mb-3">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              rows="1"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="Type your message here..."
            />
          </div>

          <button
            onClick={sendMessage}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Send Message
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportChatBox;
